import { useEffect, useRef, useState } from 'react'
import { CanceledError } from 'axios'
import { STUDENT_ASSIGNMENT_FILE_SOURCE } from 'modules/attachments/const/attachments-consts'
import useUploadFile, { FileInfoInterface } from 'modules/attachments/hooks/use-upload-datafile'
import { CourseLectureAssignmentInterface } from 'modules/course/types/course-lecture-interface'

export enum AssignmentUploadingStatusEnum {
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  Failed = 'failed',
}

export const useUploadAssignment = (assignmentFile: CourseLectureAssignmentInterface | null) => {
  const [file, setFile] = useState<Partial<FileInfoInterface & { createdAt: string }> | null>(null)
  const [lastFile, setLastFile] = useState<Partial<
    FileInfoInterface & { createdAt: string }
  > | null>(null)
  const [uploadStatus, setUploadStatus] = useState<AssignmentUploadingStatusEnum | null>(null)
  const { validateAndGetUploadOptions, uploadFile, activateFile } = useUploadFile()
  const [progress, setProgress] = useState<number | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (assignmentFile) {
      setFile({
        filename: assignmentFile.name,
        ...assignmentFile,
      })
      setLastFile({
        filename: assignmentFile.name,
        ...assignmentFile,
      })
    }
  }, [assignmentFile])

  const handleFileUpload = async (newFile: File) => {
    setLastFile(file)
    setFile({
      filename: newFile.name,
      size: newFile.size,
      type: newFile.type,
      createdAt: new Date().toISOString(),
    })
    setUploadStatus(AssignmentUploadingStatusEnum.Uploading)

    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      const { id, uploadOptions } = await validateAndGetUploadOptions({
        file: newFile,
        source: STUDENT_ASSIGNMENT_FILE_SOURCE,
        onUploadProgress: progressEvent => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setProgress(Math.round(percent * 0.1)) // 0-10%
          }
        },
        signal,
      })
      await uploadFile({
        file: newFile,
        uploadOptions,
        onUploadProgress: progressEvent => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setProgress(10 + Math.round(percent * 0.7)) // 10-80%
          }
        },
        signal,
      })
      const activatedFile = await activateFile(
        id,
        progressEvent => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setProgress(80 + Math.round(percent * 0.2)) // 80-100%
          }
        },
        signal,
      )
      setFile({ ...activatedFile[id], createdAt: new Date().toISOString() })
      setUploadStatus(AssignmentUploadingStatusEnum.Uploaded)
    } catch (e) {
      if (e instanceof CanceledError) {
        // ignore aborted upload
        return
      }

      setUploadStatus(AssignmentUploadingStatusEnum.Failed)
    }
  }

  const cancelUpload = () => {
    abortControllerRef.current?.abort()
    setFile(lastFile)
    setUploadStatus(null)
    setProgress(null)
  }

  return {
    file,
    uploadStatus,
    progress,
    handleFileUpload,
    cancelUpload,
  }
}
